import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { firstValueFrom } from "rxjs";
import { environment } from "environments/environment";
import { ReturnRequestDetail } from "../model/entities/entity-model";
import { DialogBaseComponent, DialogParams } from "../shared/dialog-base.component";
import { DomainService } from "../shared/domain.service";
import { UtilFns } from "../shared/util-fns";

export interface InspectRrdDialogParams extends DialogParams {
  rrdId: number;
}

/**
 * Pass 2 dialog for the INSPECTING phase. Operator picks one of three
 * Decisions and optionally adds notes; on success the server records the
 * Decision, transitions the RRD to ROUTING, and emits a ReturnInspected
 * JoNote. Resolves to true so the caller refreshes the row in place.
 *
 * Allowed Decisions mirror the CK_RRD_Decision CHECK constraint on the
 * ReturnRequestDetail table.
 */
@Component({
  selector: "app-inspect-rrd-dialog",
  templateUrl: "./inspect-rrd-dialog.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class InspectRrdDialogComponent extends DialogBaseComponent<InspectRrdDialogParams> implements OnInit {
  rrd: ReturnRequestDetail | null = null;

  /** Master list of all possible Decisions. The set actually offered to the
   * inspector is computed at runtime based on the order's account-owned flag
   * and the return reason's CreditAccountOwnedReturns flag. */
  private readonly allDecisions = [
    {
      value: "AcceptForCredit",
      label: "Accept for credit",
      description: "Items will be processed for credit/refund; money flows back to the customer.",
    },
    {
      value: "RestockToAccountOwned",
      label: "Restock to account-owned inventory",
      description: "Items go back into the account's owned-inventory pool. No credit issued.",
    },
    {
      value: "Reject",
      label: "Reject",
      description: "Items will be shipped back to the customer. No credit issued.",
    },
  ];

  availableDecisions = this.allDecisions;

  /** A per-fact snapshot of how the Decision matrix was evaluated. Two facts
   * drive the outcome: was the original order account-owned, and does the
   * return reason allow crediting? Rendered in the template so the inspector
   * can see how the dialog decided. */
  matrixFacts: { label: string; ok: boolean; detail: string }[] = [];
  matrixOutcome = "";

  selectedDecision: string | null = null;
  decisionNotes: string = "";
  submitting = false;
  errorMessage: string | null = null;

  @ViewChild("notesInput") notesInput?: ElementRef<HTMLTextAreaElement>;

  constructor(
    config: DynamicDialogConfig<InspectRrdDialogParams>,
    dialogRef: DynamicDialogRef,
    domainService: DomainService,
    private http: HttpClient
  ) {
    super(config, dialogRef, domainService);
  }

  async ngOnInit() {
    const r = await this.uow.fetchEntityByKey(ReturnRequestDetail, this.params.rrdId);
    this.rrd = (r?.entity as ReturnRequestDetail) ?? null;
    if (this.rrd) {
      this.computeAvailableDecisions(this.rrd);
    }
    this.isPageReady = true;
    await UtilFns.wait(1);
    this.notesInput?.nativeElement?.focus();
  }

  /**
   * Decide which Decisions to surface. Two facts drive the matrix:
   *   - Account.AllowReissuanceOrders (account manages its own inventory)
   *   - CreditAccountOwnedReturns     (return reason issues a credit on return)
   *
   * | AllowReissuance | CreditAcctOwnedReturns | Allowed                       |
   * |-----------------|------------------------|-------------------------------|
   * | false           | (irrelevant)           | AcceptForCredit, Reject       |
   * | true            | true                   | AcceptForCredit, Reject       |
   * | true            | false                  | RestockToAccountOwned, Reject |
   *
   * Reject is always available; the matrix only swaps the positive-acceptance
   * option between AcceptForCredit and RestockToAccountOwned.
   */
  private computeAvailableDecisions(rrd: ReturnRequestDetail) {
    const account = rrd.returnRequest?.joHeader?.account;
    const allowReissuance = account?.allowReissuanceOrders === true;
    const creditAcctOwned = rrd.creditAccountOwnedReturns === true;

    this.matrixFacts = [
      {
        label: "Account manages its own inventory",
        ok: allowReissuance,
        detail: allowReissuance
          ? "This account uses account-owned inventory."
          : "This account does not use account-owned inventory.",
      },
      {
        label: "Return reason allows a credit",
        ok: creditAcctOwned,
        detail: creditAcctOwned
          ? "The customer-selected return reason issues a credit on return."
          : "The customer-selected return reason restocks without issuing a credit.",
      },
    ];

    if (allowReissuance && !creditAcctOwned) {
      this.availableDecisions = this.allDecisions.filter(d => d.value !== "AcceptForCredit");
      this.matrixOutcome = "Restock to account-owned inventory (or reject) — no credit will be issued.";
    } else {
      this.availableDecisions = this.allDecisions.filter(d => d.value !== "RestockToAccountOwned");
      this.matrixOutcome = "Accept for credit (or reject) — a credit memo will be created.";
    }
  }

  selectDecision(value: string) {
    this.selectedDecision = value;
  }

  get canSubmit(): boolean {
    return this.rrd != null
      && this.selectedDecision != null
      && !this.submitting;
  }

  async confirm() {
    if (!this.canSubmit || !this.rrd) return;
    this.submitting = true;
    this.errorMessage = null;
    try {
      const actor = this.authService.getUser()?.initials ?? "SYS";
      const url = environment.apiRoot + "breeze/Uniformax/RecordInspection";
      await firstValueFrom(this.http.post(url, {
        rrdId: this.rrd.id,
        decision: this.selectedDecision,
        decisionNotes: this.decisionNotes,
        actor: actor,
      }));
      this.toastr.success(`RRD #${this.rrd.id} inspected — moved to ROUTING`);
      this.dialogRef.close(true);
    } catch (err: any) {
      const msg = err?.error?.error || err?.message || "Failed to record inspection.";
      this.errorMessage = msg;
    } finally {
      this.submitting = false;
    }
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
